import React, { useEffect, useState } from 'react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { updatePassword, deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ShieldIcon, AlertTriangleIcon, LoaderIcon, SaveIcon, XIcon, LockIcon } from 'lucide-react';
export const StudentAccountSettings = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  // Password Edit State
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  // Delete Account State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setEmail(data.email || currentUser.email || '');
        } else {
          setEmail(currentUser.email || '');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load account data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [currentUser]);
  const handleCancelPassword = () => {
    setIsEditingPassword(false);
    setNewPassword('');
    setConfirmPassword('');
  };
  const handleSavePassword = async () => {
    if (!currentUser) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsSavingPassword(true);
    try {
      await updatePassword(currentUser, newPassword);
      toast.success('Password updated successfully');
      handleCancelPassword();
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Please log out and log back in, then try again');
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setIsSavingPassword(false);
    }
  };
  const handleDeleteConfirmed = () => {
    // Close the confirmation modal and open the re-auth modal
    setShowDeleteModal(false);
    setReauthPassword('');
    setReauthError('');
    setShowReauthModal(true);
  };
  const handleCancelReauth = () => {
    setShowReauthModal(false);
    setReauthPassword('');
    setReauthError('');
  };
  const handleReauthAndDelete = async () => {
    if (!currentUser || !currentUser.email) return;
    if (!reauthPassword) {
      setReauthError('Please enter your password');
      return;
    }
    setIsDeleting(true);
    setReauthError('');
    try {
      // Step 1: Re-authenticate the user
      const credential = EmailAuthProvider.credential(currentUser.email, reauthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      // Step 2: Delete user document from Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userDocRef);
      // Step 3: Delete user from Firebase Auth
      await deleteUser(currentUser);
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error: any) {
      console.error('Error during account deletion:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setReauthError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setReauthError('Too many attempts. Please wait a moment and try again.');
      } else {
        setReauthError('Something went wrong. Please try again.');
      }
      setIsDeleting(false);
    }
  };
  // Common styles based on theme
  const cardClass = `rounded-xl p-6 shadow-sm border transition-all ${isDark ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`;
  const dangerCardClass = `rounded-xl p-6 shadow-sm border transition-all ${isDark ? 'bg-navy-800 border-red-500/20' : 'bg-white border-red-200'}`;
  const labelClass = `block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`;
  const valueClass = `text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`;
  const inputClass = `w-full px-4 py-2.5 rounded-lg border transition-colors focus:ring-2 focus:ring-green-500/50 focus:border-green-500 outline-none ${isDark ? 'bg-navy-900 border-navy-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`;
  const readOnlyInputClass = `w-full px-4 py-2.5 rounded-lg border cursor-not-allowed ${isDark ? 'bg-navy-900/50 border-navy-700 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`;
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-6823">
        <LoaderIcon className={`w-8 h-8 animate-spin ${isDark ? 'text-green-400' : 'text-green-500'}`} data-id="element-6824" />
      </div>;
  }
  return <div className="min-h-screen p-6 md:p-8" data-id="element-6825">
      <div className="max-w-[900px] mx-auto space-y-8" data-id="element-6826">
        {/* Page Header */}
        <div data-id="element-6827">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6828">
            Account Settings
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6829">
            Manage your account security and preferences.
          </p>
        </div>

        {/* Section 1: Security */}
        <div className={cardClass} data-id="element-6830">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-navy-700" data-id="element-6831">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-900 text-green-400' : 'bg-green-50 text-green-600'}`} data-id="element-6832">
              <ShieldIcon className="w-5 h-5" data-id="element-6833" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6834">
              Security
            </h2>
          </div>

          <div className="space-y-6" data-id="element-6835">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8" data-id="element-6836">
              <div data-id="element-6837">
                <span className={labelClass} data-id="element-6838">Email Address</span>
                <span className={valueClass} data-id="element-6839">{email}</span>
              </div>
              <div data-id="element-6840">
                <span className={labelClass} data-id="element-6841">Password</span>
                <span className={valueClass} data-id="element-6842">••••••••</span>
              </div>
            </div>

            {!isEditingPassword ? <div className="pt-4 border-t border-gray-200 dark:border-navy-700" data-id="element-6843">
                <button onClick={() => setIsEditingPassword(true)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`} data-id="element-6844">
                  Change Password
                </button>
              </div> : <div className="pt-4 border-t border-gray-200 dark:border-navy-700 space-y-5" data-id="element-6845">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-6846">
                  <div data-id="element-6847">
                    <label className={labelClass} data-id="element-6848">New Password</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputClass} placeholder="Enter new password" data-id="element-6849" />
                  </div>
                  <div data-id="element-6850">
                    <label className={labelClass} data-id="element-6851">Confirm Password</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputClass} placeholder="Confirm new password" data-id="element-6852" />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2" data-id="element-6853">
                  <button onClick={handleCancelPassword} disabled={isSavingPassword} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} disabled:opacity-50`} data-id="element-6854">
                    Cancel
                  </button>
                  <button onClick={handleSavePassword} disabled={isSavingPassword} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-70" data-id="element-6855">
                    {isSavingPassword ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-6856" /> : <SaveIcon className="w-4 h-4" data-id="element-6857" />}
                    {isSavingPassword ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>}
          </div>
        </div>

        {/* Section 2: Danger Zone */}
        <div className={dangerCardClass} data-id="element-6858">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-red-200 dark:border-red-500/20" data-id="element-6859">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}`} data-id="element-6860">
              <AlertTriangleIcon className="w-5 h-5" data-id="element-6861" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`} data-id="element-6862">
              Danger Zone
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-6863">
            <div data-id="element-6864">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6865">
                Permanently delete your account and all associated data.
              </p>
            </div>
            <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors whitespace-nowrap" data-id="element-6866">
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-6867">
          <div className={`w-full max-w-md rounded-xl p-6 shadow-xl ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-6868">
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6869">
              Delete Account
            </h3>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6870">
              Are you sure you want to delete your account? This action cannot
              be undone.
            </p>
            <div className="flex items-center justify-end gap-3" data-id="element-6871">
              <button onClick={() => setShowDeleteModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6872">
                Cancel
              </button>
              <button onClick={handleDeleteConfirmed} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors" data-id="element-6873">
                Delete Account
              </button>
            </div>
          </div>
        </div>}

      {/* Re-authentication Modal */}
      {showReauthModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-6874">
          <div className={`w-full max-w-md rounded-xl p-6 shadow-xl ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-6875">
            <div className="flex items-center gap-3 mb-4" data-id="element-6876">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-navy-900 text-amber-400' : 'bg-amber-50 text-amber-600'}`} data-id="element-6877">
                <LockIcon className="w-5 h-5" data-id="element-6878" />
              </div>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6879">
                Confirm Your Identity
              </h3>
            </div>
            <p className={`mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6880">
              For security, please re-enter your password to confirm account
              deletion.
            </p>

            <div className="mb-4" data-id="element-6881">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6882">
                Password
              </label>
              <input type="password" value={reauthPassword} onChange={e => {
            setReauthPassword(e.target.value);
            setReauthError('');
          }} onKeyDown={e => {
            if (e.key === 'Enter' && !isDeleting) {
              handleReauthAndDelete();
            }
          }} className={`w-full px-4 py-2.5 rounded-lg border transition-colors focus:ring-2 focus:ring-red-500/50 focus:border-red-500 outline-none ${isDark ? 'bg-navy-900 border-navy-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`} placeholder="Enter your password" autoFocus disabled={isDeleting} data-id="element-6883" />
              {reauthError && <p className="mt-2 text-sm text-red-500 font-medium" data-id="element-6884">
                  {reauthError}
                </p>}
            </div>

            <div className="flex items-center justify-end gap-3" data-id="element-6885">
              <button onClick={handleCancelReauth} disabled={isDeleting} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} disabled:opacity-50`} data-id="element-6886">
                Cancel
              </button>
              <button onClick={handleReauthAndDelete} disabled={isDeleting || !reauthPassword} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-70" data-id="element-6887">
                {isDeleting ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-6888" /> : null}
                {isDeleting ? 'Deleting...' : 'Confirm & Delete'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
};